import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  createAnalysis,
  createAnalysisFromDocuments,
  getProjects,
  RunnerUnavailableError,
  type ProjectSummary,
} from '../api.js';
import { AppHeader } from '../components/AppHeader.js';
import { DealForm } from '../components/DealForm.js';
import { DocUpload } from '../components/DocUpload.js';
import { useSession } from '../useSession.js';
import { canSubmitAnalysis } from '../roles.js';
import { filesToUploadDocuments } from '../upload.js';
import type { deriveDeal } from '../deal-form.js';

type Mode = 'upload' | 'manual';

/** The authed home: drop documents OR enter a deal, run a premium analysis, route to its result. */
export function AppShell() {
  const navigate = useNavigate();
  const session = useSession();
  const [mode, setMode] = useState<Mode>('upload');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);

  // Load the tenant's projects for the upload picker. getProjects never throws (→ [] on any failure),
  // so the picker simply falls back to "new project" and the form is never blocked.
  useEffect(() => {
    let cancelled = false;
    void getProjects().then((p) => {
      if (!cancelled) setProjects(p);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  function fail(err: unknown) {
    if (err instanceof RunnerUnavailableError) setError(err.message);
    else setError(err instanceof Error ? err.message : 'Could not start the analysis.');
    setSubmitting(false);
  }

  async function onSubmitDeal(
    payload: ReturnType<typeof deriveDeal> & { contactConsent: boolean },
  ) {
    setSubmitting(true);
    setError(null);
    try {
      const id = await createAnalysis(payload.deal, {
        // Always sent from this surface — it offers the box, so it reports the client's answer
        // either way. Absent would mean "never asked", which would be untrue here.
        contactConsent: payload.contactConsent,
        reviewerContext: payload.reviewerContext,
        sponsorName: payload.sponsorName,
        sponsorContext: payload.sponsorContext,
      });
      void navigate(`/analyses/${id}`);
    } catch (err) {
      fail(err);
    }
  }

  async function onSubmitDocs(payload: {
    files: File[];
    reviewerContext: string;
    project: { code: string; name?: string | null } | null;
    sponsorName: string;
    sponsorContext: string;
    contactConsent: boolean;
  }) {
    setSubmitting(true);
    setError(null);
    try {
      const documents = await filesToUploadDocuments(payload.files);
      const id = await createAnalysisFromDocuments(documents, {
        // Always sent — see the manual door above.
        contactConsent: payload.contactConsent,
        reviewerContext: payload.reviewerContext || null,
        project: payload.project,
        // Blank → null, so an untouched field is an absent name rather than an empty search.
        sponsorName: payload.sponsorName || null,
        // Same: blank → null, so an untouched field reads as "no sponsor evidence was supplied"
        // rather than as an empty corpus that was.
        sponsorContext: payload.sponsorContext || null,
      });
      void navigate(`/analyses/${id}`);
    } catch (err) {
      fail(err);
    }
  }

  if (session.status !== 'authed') {
    return <div className="app__loading">Loading your workspace…</div>;
  }

  /*
    🔴 THE LANDING PAGE WAS THE WORST OF THE FIVE. `/` is what a client sees the moment they sign in
    — and `*` redirects here too — so a `viewer` was greeted by an H1 reading "Run a deal analysis"
    above a form that 403s (the gate `#768` put on the API but not on the screen). The first thing
    the product said to a paying client was: do the one thing your role forbids.

    ⚠️ REDIRECTED, NOT EMPTIED. Hiding the form would leave a client on a page with a heading, a
    paragraph about the engine and nothing to do — worse than the wrong page, because it looks
    broken rather than deliberate. They go to their DEAL LIST, which by Brad's 2026-08-21 ruling
    ("clients read the reports GGP produces") is the client's actual product.

    ⚠️ `replace` so Back does not bounce them off their own list and straight back here. A redirect
    that traps the Back button reads as the app fighting the user.

    ⚠️ THIS HIDES; IT DOES NOT PROTECT. `POST /analyses` refuses a viewer whatever the browser
    renders — see `roles.ts`. If this check and the server ever disagree, the server is right.
  */
  if (!canSubmitAnalysis(session.claims)) return <Navigate to="/analyses" replace />;

  const memberships = session.memberships;
  return (
    <div className="app">
      <AppHeader claims={session.claims} />
      <main className="app__main">
        <div className="app__intro">
          <h1>Run a deal analysis</h1>
          <p className="sub">
            Institutional-grade underwriting — the deterministic engine bundle plus the model-judged
            Scorecard, Sensitivity, Lender, Sponsor, and IC memo.
          </p>
        </div>

        <div className="modetabs" role="tablist" aria-label="How to start an analysis">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'upload'}
            className={`modetab${mode === 'upload' ? ' modetab--active' : ''}`}
            onClick={() => !submitting && setMode('upload')}
          >
            Upload documents
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'manual'}
            className={`modetab${mode === 'manual' ? ' modetab--active' : ''}`}
            onClick={() => !submitting && setMode('manual')}
          >
            Enter manually
          </button>
        </div>

        {mode === 'upload' ? (
          <DocUpload
            submitting={submitting}
            error={error}
            projects={projects}
            onSubmit={(p) => void onSubmitDocs(p)}
          />
        ) : (
          <DealForm submitting={submitting} error={error} onSubmit={(p) => void onSubmitDeal(p)} />
        )}

        <p className="app__rls muted">
          You have access to <strong>{memberships ? memberships.memberships.length : 0}</strong>{' '}
          workspace
          {memberships && memberships.memberships.length === 1 ? '' : 's'}.
        </p>
      </main>
    </div>
  );
}
